/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { SEC_TO_MS_CONVERSION } from '../../../../../utils/DateTimeUtils';
import { chartSeriesContent, ChartItemKeys, ChartType } from '../ChartSeriesItems';
import { BEAM_TYPE, getBeamTypeFromValue } from '../../../../../utils/BeamUtils';
import TerminalHistory from '../../../../../shared/entities/terminalHistory';
import has from 'lodash/has';
import { isNil } from 'lodash';
import { STALE_DATA_GAP_SEC } from '../../../../../utils/GlobalUtils';

enum TimelineSegmentState {
  OFFLINE = 'OFFLINE',
  ONLINE = 'ONLINE',
  STANDY = 'STANDBY',
}

const STANDBY_LOWER_CASE = 'standby';

const CHART_SERIES_METADATA = chartSeriesContent[ChartItemKeys.timeline][0];
export const ONLINE_SERIES_KEY = `${CHART_SERIES_METADATA.key}_${TimelineSegmentState.ONLINE}`;
export const OFFLINE_SERIES_KEY = `${CHART_SERIES_METADATA.key}_${TimelineSegmentState.OFFLINE}`;
export const STANDBY_SERIES_KEY = `${CHART_SERIES_METADATA.key}_${TimelineSegmentState.STANDY}`;
export const ANNOTATIONS_SERIES_KEY = `${CHART_SERIES_METADATA.key}_annotations`;

const DEFAULT_MARKER_Y_VALUE = 5;
const MULTIPLE_TIMELINE_MARKER_Y_VALUE = 7;

export const TOOLTIP_HIDE_DELAY_MS = 1000;

const SVG_PLACEHOLDER = 'SVG_PLACEHOLDER';
export const SVG_DATA_PREFIX = 'data:image/svg+xml;charset=utf-8,';
const INLINE_SVG_DATA_URI = `url(${SVG_DATA_PREFIX}${SVG_PLACEHOLDER})`;

export const SVG_26x26 = '<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26">';
export const CLUSTER_TEXT = '<text x="9" y="18" class="font">';

const KA_SPOT_BEAM_HANDOVER_ICON_SVG = INLINE_SVG_DATA_URI.replace(
  SVG_PLACEHOLDER,
  encodeURIComponent(
    `${SVG_26x26}<path fill="none" d="M0 0h26v26H0V0z"/><circle cx="13" cy="13" r="13" fill="#ffffff"/><circle cx="13" cy="13" r="12" fill="#5EA215"/><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" x="4" y="4"><path d="M0 0h24v24H0z" fill="none"/><path d="M15 4c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"/><path d="M3 12c0-2.61 1.67-4.83 4-5.65V4.26C3.55 5.15 1 8.27 1 12s2.55 6.85 6 7.74v-2.09c-2.33-.82-4-3.04-4-5.65z"/></svg></svg>`,
  ),
);

const KA_TO_KA_HANDOVER_ICON_SVG = INLINE_SVG_DATA_URI.replace(
  SVG_PLACEHOLDER,
  encodeURIComponent(
    `${SVG_26x26}<path fill="none" d="M0 0h26v26H0V0z"/><circle cx="13" cy="13" r="13" fill="#ffffff"/><circle cx="13" cy="13" r="12" fill="#FFB029"/><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" x="4" y="4"><path d="M0 0h24v24H0z" fill="none"/><path d="M15 4c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"/><path d="M3 12c0-2.61 1.67-4.83 4-5.65V4.26C3.55 5.15 1 8.27 1 12s2.55 6.85 6 7.74v-2.09c-2.33-.82-4-3.04-4-5.65z"/></svg></svg>`,
  ),
);

const HANDOVER_ICON_SVG = INLINE_SVG_DATA_URI.replace(
  SVG_PLACEHOLDER,
  encodeURIComponent(
    `${SVG_26x26}<path fill="none" d="M0 0h26v26H0V0z"/><circle cx="13" cy="13" r="13" fill="#ffffff"/><circle cx="13" cy="13" r="12" fill="#FFB029"/><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" x="4" y="4"><path d="M0 0h24v24H0z" fill="none"/><path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z"/></svg></svg>`,
  ),
);

const NETWORK_HANDOVER_ICON_SVG = INLINE_SVG_DATA_URI.replace(
  SVG_PLACEHOLDER,
  encodeURIComponent(
    `${SVG_26x26}<path fill="none" d="M0 0h26v26H0V0z"/><circle cx="13" cy="13" r="13" fill="#ffffff"/><circle cx="13" cy="13" r="12" fill="#FFB029"/><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" x="4" y="4"><path d="M0 0h24v24H0z" fill="none"/><path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z"/></svg></svg>`,
  ),
);

const NETWORK_CONNECTION_ICON_SVG = INLINE_SVG_DATA_URI.replace(
  SVG_PLACEHOLDER,
  encodeURIComponent(
    `${SVG_26x26}<path fill="none" d="M0 0h26v26H0V0z"/><circle cx="13" cy="13" r="13" fill="#ffffff"/><circle cx="13" cy="13" r="12" fill="#5EA215"/><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" x="4" y="4"><path d="M0 0h24v24H0z" fill="none"/><path d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z"/></svg></svg>`,
  ),
);

const NETWORK_CONNECTION_LOST_ICON_SVG = INLINE_SVG_DATA_URI.replace(
  SVG_PLACEHOLDER,
  encodeURIComponent(
    `${SVG_26x26}<path fill="none" d="M0 0h26v26H0V0z"/><circle cx="13" cy="13" r="13" fill="#ffffff"/><circle cx="13" cy="13" r="12" fill="#C3CDD5"/><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" x="4" y="4"><path d="M0 0h24v24H0z" fill="none"/><path d="M10 6.35V4.26c-.8.21-1.55.54-2.23.96l1.46 1.46c.25-.12.5-.24.77-.33zm-7.14-.94l2.36 2.36C4.45 8.99 4 10.44 4 12c0 2.21.91 4.2 2.36 5.64L4 20h6v-6l-2.24 2.24C6.68 15.15 6 13.66 6 12c0-1 .25-1.94.68-2.77l8.08 8.08c-.25.13-.5.25-.77.34v2.09c.8-.21 1.55-.54 2.23-.96l2.36 2.36 1.27-1.27L4.14 4.14 2.86 5.41zM20 4h-6v6l2.24-2.24C17.32 8.85 18 10.34 18 12c0 1-.25 1.94-.68 2.77l1.46 1.46C19.55 15.01 20 13.56 20 12c0-2.21-.91-4.2-2.36-5.64L20 4z"/></svg></svg>`,
  ),
);

const CLUSTER_INNER_CIRCLE = `<path fill="none" d="M0 0h26v26H0V0z"/><circle cx="13" cy="13" r="13" fill="#ffffff"/><circle cx="13" cy="13" r="12" fill="#141D24"/><style>.font {font-family: 'Source Sans Pro', sans-serif;font-size: 14px;fill: white;}</style>${CLUSTER_TEXT}</text>`;

export const CLUSTER_ICON_SVG = INLINE_SVG_DATA_URI.replace(
  SVG_PLACEHOLDER,
  encodeURIComponent(`${SVG_26x26}${CLUSTER_INNER_CIRCLE}</svg>`),
);

export const CLUSTER_ICON_SVG_NO_URL = `${SVG_DATA_PREFIX}${encodeURIComponent(
  `${SVG_26x26}${CLUSTER_INNER_CIRCLE}</svg>`,
)}`;

export const CLUSTER_ICON_SVG_WITH_HOVER_RING = `${SVG_DATA_PREFIX}${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="19" fill="none" stroke="#ffffff" stroke-width="2"/><svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" x="7" y="7">${CLUSTER_INNER_CIRCLE}</svg></svg>`,
)}`;

interface TimelinePoint extends Highcharts.Point {
  network?: string;
  beam?: string;
  className?: string;
}

export interface AnnotationPoint extends Highcharts.PointOptionsObject {
  id?: string;
  custom: Handover;
  clusteredData?: AnnotationPoint;
}

interface Handover {
  currentBeamValue?: string;
  currentViasatBeamValue?: string;
  nextBeamValue?: string;
  nextViasatBeamValue?: string;
  currentBeamType?: BEAM_TYPE;
  nextBeamType?: BEAM_TYPE;
}

export interface ChartSegment {
  start: number;
  end: number;
  type: TimelineSegmentState;
  // if this is the most recent and current segment on the timeline
  current?: boolean;
  // if this is the start or first segment of the terminal history
  firstSegment?: boolean;
}

// gets the initial chart series data
export const getInitialChartData = (): (Highcharts.SeriesXrangeOptions | Highcharts.SeriesScatterOptions)[] => {
  const commonSeriesOptions: Highcharts.SeriesXrangeOptions = {
    type: ChartType.xrange,
    // @ts-ignore Highcharts 7.2.0 does not have linecap in the Typescript definition, but the API still has it
    linecap: 'butt',
    borderRadius: 0,
    pointPadding: 0,
    showInLegend: true,
    colorByPoint: false,
    grouping: false,
  };

  const onlineSeriesData: Highcharts.SeriesXrangeOptions = {
    ...commonSeriesOptions,
    name: 'Active',
    id: ONLINE_SERIES_KEY,
    className: 'ftv__charts__timeline--connected',
    data: [],
  };

  const standbySeriesData: Highcharts.SeriesXrangeOptions = {
    ...commonSeriesOptions,
    name: 'Standby',
    id: STANDBY_SERIES_KEY,
    className: 'ftv__charts__timeline--standby',
    showInLegend: false, // will be shown when the terminal installation type is Ku plus Ka
    data: [],
  };

  const offlineSeriesData: Highcharts.SeriesXrangeOptions = {
    ...commonSeriesOptions,
    name: 'Disconnected',
    id: OFFLINE_SERIES_KEY,
    className: 'ftv__charts__timeline--disconnected',
    data: [],
  };

  const markerScatterSeries: Highcharts.SeriesScatterOptions = {
    type: 'scatter',
    name: 'markers',
    id: ANNOTATIONS_SERIES_KEY,
    className: 'ftv__charts__timeline--marker',
    data: [{ x: 0, y: DEFAULT_MARKER_Y_VALUE }], // Needed to get around issue for the marker clusters: https://github.com/highcharts/highcharts/issues/13981
    showInLegend: false,
    zIndex: 7,
  };

  return [onlineSeriesData, standbySeriesData, offlineSeriesData, markerScatterSeries];
};

/**
 * Get the state (online/offline) of a segment from the network origin.
 * @param networkOrigin the network origin to determine if a terminal is online
 */
export const getSegmentStateFromNetworkOriginAndConnStatus = (networkOrigin?: string, connectivityStatus?: string) => {
  const isOnline = !!networkOrigin;
  const isStandby = isOnline && connectivityStatus === STANDBY_LOWER_CASE;

  return isOnline
    ? isStandby
      ? TimelineSegmentState.STANDY
      : TimelineSegmentState.ONLINE
    : TimelineSegmentState.OFFLINE;
};

// returns an object with a list of segments for the different states of the timeline chart
export const getDataFromHistory = (
  terminalHistory: { [key: number]: TerminalHistory },
  chartGapSeconds: number,
  chartsXMin: number | null,
  chartsXMax: number | null,
): { [key in TimelineSegmentState]: ChartSegment[] } => {
  const onlineAndOfflineSegments: { [key in TimelineSegmentState]: ChartSegment[] } = {
    [TimelineSegmentState.ONLINE]: [],
    [TimelineSegmentState.OFFLINE]: [],
    [TimelineSegmentState.STANDY]: [],
  };
  const sortedTerminalHistoryKeys: string[] = Object.keys(terminalHistory).sort(
    (a, b) => terminalHistory[a].sourceTime - terminalHistory[b].sourceTime,
  );
  const beamNameFilteredTermHistKeys: string[] = sortedTerminalHistoryKeys.filter((timestamp: string) => {
    // NOTE: network origin will be present when the terminal is online
    return terminalHistory[timestamp].networkOrigin;
  });

  if (beamNameFilteredTermHistKeys.length === 0 && chartsXMin && chartsXMax) {
    // add an offline segment from the chart min to max
    onlineAndOfflineSegments[TimelineSegmentState.OFFLINE].push({
      start: chartsXMin,
      end: chartsXMax,
      type: TimelineSegmentState.OFFLINE,
      firstSegment: true,
      current: true,
    });

    return onlineAndOfflineSegments;
  } else if (beamNameFilteredTermHistKeys.length === 0) {
    return onlineAndOfflineSegments;
  }

  let startTermHist = terminalHistory[sortedTerminalHistoryKeys[0]];
  if (has(terminalHistory[beamNameFilteredTermHistKeys[0]], 'sourceTime')) {
    let segment: ChartSegment = {
      start: chartsXMin ? chartsXMin : startTermHist.sourceTime,
      end: 0,
      type: getSegmentStateFromNetworkOriginAndConnStatus(
        startTermHist.networkOrigin,
        startTermHist.connectivityStatus,
      ),
      firstSegment: true,
    };

    // if the current data does not have a beamName then start at the first index of
    // the filtered data
    if (
      beamNameFilteredTermHistKeys[0] !== sortedTerminalHistoryKeys[0] ||
      (chartsXMin && chartsXMin !== parseInt(sortedTerminalHistoryKeys[0]))
    ) {
      startTermHist = terminalHistory[beamNameFilteredTermHistKeys[0]];
      segment.end = startTermHist.sourceTime;
      segment.type = TimelineSegmentState.OFFLINE;

      // push initial offline segment
      onlineAndOfflineSegments[TimelineSegmentState.OFFLINE].push(segment);

      // start the intial online segment
      segment = {
        start: startTermHist.sourceTime,
        end: 0, // to be filled in later
        type: getSegmentStateFromNetworkOriginAndConnStatus(
          startTermHist.networkOrigin,
          startTermHist.connectivityStatus,
        ),
      };
    }

    let currentData = startTermHist;

    for (let idx = 0; idx < beamNameFilteredTermHistKeys.length; idx++) {
      let nextTimestamp = beamNameFilteredTermHistKeys[idx + 1];
      if (nextTimestamp) {
        let nextData = terminalHistory[nextTimestamp];
        if (nextData.sourceTime - currentData.sourceTime >= chartGapSeconds) {
          // offline period found because of gap in data. Push previous segment and show gap as offline.
          segment.end = currentData.sourceTime;
          onlineAndOfflineSegments[segment.type].push(segment);
          segment = {
            start: currentData.sourceTime,
            end: nextData.sourceTime,
            type: TimelineSegmentState.OFFLINE,
          };
          onlineAndOfflineSegments[TimelineSegmentState.OFFLINE].push(segment); // push offline segment
          segment = {
            start: nextData.sourceTime,
            end: 0,
            type: getSegmentStateFromNetworkOriginAndConnStatus(nextData.networkOrigin, nextData.connectivityStatus),
          };
        } else if (
          currentData.beamDisplayName !== nextData.beamDisplayName ||
          currentData.connectivityStatus !== nextData.connectivityStatus
        ) {
          segment.end = nextData.sourceTime;
          onlineAndOfflineSegments[segment.type].push(segment);
          segment = {
            start: nextData.sourceTime,
            end: 0,
            type: getSegmentStateFromNetworkOriginAndConnStatus(nextData.networkOrigin, nextData.connectivityStatus),
          };
        }
        currentData = nextData;
      } else {
        // the most recent segment that is online
        segment.end = currentData.sourceTime;
        onlineAndOfflineSegments[segment.type].push(segment);
      }
    }

    const finalTermalHistory = terminalHistory[sortedTerminalHistoryKeys[sortedTerminalHistoryKeys.length - 1]];
    const finalOnlineTerminalHistory =
      terminalHistory[beamNameFilteredTermHistKeys[beamNameFilteredTermHistKeys.length - 1]];

    // check to see if there is a final offline segment
    if (
      (finalTermalHistory &&
        finalOnlineTerminalHistory &&
        finalTermalHistory.sourceTime !== finalOnlineTerminalHistory.sourceTime) ||
      (chartsXMax &&
        chartsXMax !== finalOnlineTerminalHistory.sourceTime &&
        chartsXMax - finalOnlineTerminalHistory.sourceTime >= chartGapSeconds)
    ) {
      segment = {
        start: finalOnlineTerminalHistory.sourceTime,
        end: chartsXMax ? chartsXMax : finalTermalHistory.sourceTime,
        type: TimelineSegmentState.OFFLINE,
        current: true,
      };
      onlineAndOfflineSegments[TimelineSegmentState.OFFLINE].push(segment);
    } else if (finalTermalHistory) {
      const finalSegmentState = getSegmentStateFromNetworkOriginAndConnStatus(
        finalTermalHistory.networkOrigin,
        finalTermalHistory.connectivityStatus,
      );
      const finalSegment =
        onlineAndOfflineSegments[finalSegmentState][onlineAndOfflineSegments[finalSegmentState].length - 1];

      // mark the most recent online segment as current
      if (finalSegment) {
        finalSegment.current = true;

        // since there is not an offline gap use the chart max if possible
        // otherwise use the most current data timestamp that was already set
        finalSegment.end = chartsXMax ? chartsXMax : finalTermalHistory.sourceTime;
      }
    }
  }

  return onlineAndOfflineSegments;
};

// get the highcharts annotation point from the segment data
export const getAnnotationFromSegmentData = (
  timestampSeconds: number,
  currentBeamValue?: string,
  currentViasatBeamValue?: string,
  nextBeamValue?: string,
  nextViasatBeamValue?: string,
  currentBeamType?: BEAM_TYPE,
  nextBeamType?: BEAM_TYPE,
  shouldOffsetAnnotation?: boolean,
): Partial<AnnotationPoint> => {
  let timestampMs = timestampSeconds * SEC_TO_MS_CONVERSION;
  let marker: Highcharts.PointMarkerOptionsObject = {
    symbol: HANDOVER_ICON_SVG,
  };

  const isSpotBeamHandover =
    currentBeamType === BEAM_TYPE.KA &&
    nextBeamType === BEAM_TYPE.KA &&
    !isNil(currentViasatBeamValue) &&
    !isNil(nextViasatBeamValue) &&
    currentViasatBeamValue === nextViasatBeamValue;

  const isKaToKaHandover =
    currentBeamType === BEAM_TYPE.KA &&
    nextBeamType === BEAM_TYPE.KA &&
    !isNil(currentViasatBeamValue) &&
    !isNil(nextViasatBeamValue) &&
    currentViasatBeamValue !== nextViasatBeamValue;

  if (isSpotBeamHandover) {
    marker.symbol = KA_SPOT_BEAM_HANDOVER_ICON_SVG;
  } else if (isKaToKaHandover) {
    marker.symbol = KA_TO_KA_HANDOVER_ICON_SVG;
  } else if (currentBeamType === BEAM_TYPE.OFFLINE) {
    marker.symbol = NETWORK_CONNECTION_ICON_SVG;
  } else if (nextBeamType === BEAM_TYPE.OFFLINE) {
    marker.symbol = NETWORK_CONNECTION_LOST_ICON_SVG;
  } else if (currentBeamType !== nextBeamType) {
    marker.symbol = NETWORK_HANDOVER_ICON_SVG;
  }

  return {
    id: timestampMs.toString(),
    x: timestampMs,
    y: shouldOffsetAnnotation ? MULTIPLE_TIMELINE_MARKER_Y_VALUE : DEFAULT_MARKER_Y_VALUE,
    // custom data for the tooltip to use
    custom: {
      currentBeamValue,
      currentViasatBeamValue,
      nextBeamValue,
      nextViasatBeamValue,
      currentBeamType,
      nextBeamType,
    },
    marker,
  };
};

// determine if an annotation is needed and if so, then return a highcharts point for that annotation
const getAnnotationForPointIfNeeded = (
  segment: ChartSegment,
  terminalHistory: { [key: number]: TerminalHistory },
  shouldShowFirstConnectionEstablishedAnnotation: boolean,
  shouldOffsetAnnotationForMultipleTimeline: boolean,
): Partial<AnnotationPoint>[] => {
  const currentTermHist = terminalHistory[segment.start] ? terminalHistory[segment.start] : ({} as TerminalHistory);
  const currentBeamValue = currentTermHist.beamDisplayName;
  const currentViasatBeamValue = currentTermHist.viasatBeamName;

  const nextTermHist = terminalHistory[segment.end] ? terminalHistory[segment.end] : ({} as TerminalHistory);
  const nextBeamValue = nextTermHist.beamDisplayName;
  const nextViasatBeamValue = nextTermHist.viasatBeamName;

  const isBeamSwitch = currentBeamValue !== nextBeamValue;

  let currentBeamType = getBeamTypeFromValue(currentBeamValue, currentTermHist.networkOrigin);
  let nextBeamType = getBeamTypeFromValue(nextBeamValue, nextTermHist.networkOrigin);

  if (segment.type === TimelineSegmentState.OFFLINE) {
    const connectionLostAnnotation = getAnnotationFromSegmentData(
      segment.start,
      currentBeamValue,
      currentViasatBeamValue,
      undefined,
      undefined,
      currentBeamType,
      BEAM_TYPE.OFFLINE,
      shouldOffsetAnnotationForMultipleTimeline,
    );
    const connectionEstablishedAnnotation = getAnnotationFromSegmentData(
      segment.end,
      undefined,
      undefined,
      nextBeamValue,
      nextViasatBeamValue,
      BEAM_TYPE.OFFLINE,
      nextBeamType,
      shouldOffsetAnnotationForMultipleTimeline,
    );

    // do not show an annotation for connection lost if there is no beam value for the previous segment
    if (isNil(currentBeamValue) && isNil(nextBeamValue)) {
      return [];
    } else if (isNil(nextBeamValue)) {
      return [connectionLostAnnotation];
    } else if (isNil(currentBeamValue)) {
      return [connectionEstablishedAnnotation];
    }

    return [connectionLostAnnotation, connectionEstablishedAnnotation];
  } else {
    const onlineAnnotations = [];

    if (
      shouldShowFirstConnectionEstablishedAnnotation &&
      segment.firstSegment &&
      (segment.type === TimelineSegmentState.ONLINE || segment.type === TimelineSegmentState.STANDY)
    ) {
      // connection established annotation for the start of the timeline
      onlineAnnotations.push(
        getAnnotationFromSegmentData(
          segment.start,
          undefined,
          undefined,
          currentBeamValue,
          currentViasatBeamValue,
          BEAM_TYPE.OFFLINE,
          currentBeamType,
          shouldOffsetAnnotationForMultipleTimeline,
        ),
      );
    }

    if (isBeamSwitch && !segment.current) {
      // add an annotation for the beam change
      onlineAnnotations.push(
        getAnnotationFromSegmentData(
          segment.end,
          currentBeamValue,
          currentViasatBeamValue,
          nextBeamValue,
          nextViasatBeamValue,
          currentBeamType,
          nextBeamType,
          shouldOffsetAnnotationForMultipleTimeline,
        ),
      );
    }

    return onlineAnnotations;
  }
};

// get the handover annotations from the online and offline segments of the chart
export const getAnnotations = (
  segments: { [key in TimelineSegmentState]: ChartSegment[] },
  terminalHistory: { [key: number]: TerminalHistory },
  isMultipleTimeline = false,
): Partial<AnnotationPoint>[] => {
  const annotations: Partial<AnnotationPoint>[] = [];
  const sortedTimestamps = Object.keys(terminalHistory).sort();
  let isLongerThanStaleGap =
    sortedTimestamps.length >= 2 &&
    parseInt(sortedTimestamps[sortedTimestamps.length - 1]) - parseInt(sortedTimestamps[0]) >= STALE_DATA_GAP_SEC;

  let annotationFromSegmentFunc = (segment: ChartSegment) => {
    let tempAnnotations = getAnnotationForPointIfNeeded(
      segment,
      terminalHistory,
      !isLongerThanStaleGap,
      isMultipleTimeline,
    );

    tempAnnotations.forEach(ann => annotations.push(ann));
  };

  segments.OFFLINE.forEach(annotationFromSegmentFunc);
  segments.ONLINE.forEach(annotationFromSegmentFunc);
  segments.STANDBY.forEach(annotationFromSegmentFunc);

  return annotations;
};

// helper function that converts a segment into a highcharts formatted point
const getHighchartsPointFromSegment = (
  segment: ChartSegment,
  terminalHistory: { [key: number]: TerminalHistory },
  yAxisValue: number = 0,
): Partial<TimelinePoint> => {
  const startTermHist = terminalHistory[segment.start];
  const isSegmentOnline = segment.type === TimelineSegmentState.ONLINE || segment.type === TimelineSegmentState.STANDY;
  const network = isSegmentOnline && startTermHist ? startTermHist.networkOrigin : undefined;
  const beam = isSegmentOnline && startTermHist ? startTermHist.beamDisplayName : undefined;

  return {
    x: segment.start * SEC_TO_MS_CONVERSION,
    x2: segment.end * SEC_TO_MS_CONVERSION,
    y: yAxisValue,
    network,
    beam,
  };
};

// get the highcharts points from the offline or online segments of the chart
export const getHighchartsSegments = (
  segments: ChartSegment[],
  terminalHistory: { [key: number]: TerminalHistory },
  yAxisValue: number = 0,
): Partial<Highcharts.Point>[] => {
  const dataToRender: Partial<Highcharts.Point>[] = [];
  segments.forEach((seg: ChartSegment, idx: number) => {
    dataToRender.push(getHighchartsPointFromSegment(seg, terminalHistory, yAxisValue));
  });

  return dataToRender;
};
